import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Bookmark, Trash2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * SavedDrillsCard (iter 72p)
 *
 * Dashboard widget that lists the current workspace's saved drill queries
 * ("Meals this quarter", "AR aged 90+", etc.). Each card is one click →
 * opens the shared DrillDownModal with the saved params.
 *
 * Backend endpoints (see /app/backend/server.py iter 72p):
 *   GET    /api/saved-drills
 *   POST   /api/saved-drills   { name, params, currency }
 *   DELETE /api/saved-drills/:id
 * Saving is done from inside the DrillDownModal itself; this widget only
 * lists + opens + deletes.
 */
export default function SavedDrillsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);  // {title, params, currency}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saved-drills");
      setItems(data || []);
    } catch (e) {
      // Non-blocking — dashboard should still render if this endpoint 500s.
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${name}" from your saved drills?`)) return;
    try {
      await api.delete(`/saved-drills/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  if (!loading && items.length === 0) {
    // Empty state — one small hint card, no visual weight.
    return (
      <div data-testid="saved-drills-empty"
           className="card-flat p-4 border-dashed border-[var(--divider)] text-xs text-[var(--muted-text)] flex items-center gap-2">
        <Bookmark className="w-3.5 h-3.5" />
        <span>Open any report drill-down and hit <span className="font-medium">Save</span> to pin it as a shortcut here.</span>
      </div>
    );
  }

  return (
    <>
      <div className="card-flat p-4" data-testid="saved-drills-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-2">
            <Bookmark className="w-3.5 h-3.5" /> My saved drills
          </div>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-[var(--muted-text)]" />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`saved-drill-${it.id}`}
              onClick={() => setOpen({ title: it.name, params: it.params, currency: it.currency })}
              className="group text-left rounded-lg border border-[var(--divider)] bg-[var(--card)] hover:border-[var(--brand)] hover:bg-[var(--hover)] p-3 transition-colors relative"
            >
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                     style={{ background: "#EEF7F2", color: "#2F6B4A" }}>
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mt-0.5 truncate">
                    {it.params?.kind}
                    {it.params?.category ? ` · ${it.params.category}` : ""}
                    {it.params?.customer_id ? ` · customer` : ""}
                    {it.params?.vendor_id ? ` · vendor` : ""}
                    {it.params?.start ? ` · ${it.params.start}→${it.params.end || "…"}` : ""}
                    <span className="ml-1">· {it.currency}</span>
                  </div>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => remove(it.id, it.name, e)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && remove(it.id, it.name, e)}
                  data-testid={`saved-drill-delete-${it.id}`}
                  aria-label={`Delete ${it.name}`}
                  title="Delete this saved drill"
                  className="opacity-0 group-hover:opacity-100 text-[var(--muted-text)] hover:text-[#B4283A] transition-opacity p-0.5 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <DrillDownModal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.title}
        params={open?.params}
        currency={open?.currency || "USD"}
      />
    </>
  );
}
